import { DEFAULT_BRANDING_CONFIG } from "../constants/defaultBranding";
import type { BrandingConfig, HeaderLogoConfig } from "../types";
import { normalizeThemeAndColors } from "./colors";

function normalizeNullableString(
  value: unknown,
  fallback: string | null,
): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function normalizeRequiredString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function normalizeBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function normalizeHeaderLogo(value: unknown): HeaderLogoConfig {
  const fallback = DEFAULT_BRANDING_CONFIG.brandIdentity.headerLogo;

  if (!value || typeof value !== "object") {
    return fallback;
  }

  const logo = value as Partial<HeaderLogoConfig>;
  return {
    dark: normalizeNullableString(logo.dark, fallback.dark),
    light: normalizeNullableString(logo.light, fallback.light),
  };
}

export function normalizeBrandingConfig(
  config?: Partial<BrandingConfig> | null,
): BrandingConfig {
  const fallback = DEFAULT_BRANDING_CONFIG;

  return {
    brandIdentity: {
      favicon: normalizeNullableString(
        config?.brandIdentity?.favicon,
        fallback.brandIdentity.favicon,
      ),
      headerLogo: normalizeHeaderLogo(config?.brandIdentity?.headerLogo),
      title: normalizeNullableString(
        config?.brandIdentity?.title,
        fallback.brandIdentity.title,
      ),
    },
    loginScreen: {
      logo: normalizeNullableString(
        config?.loginScreen?.logo,
        fallback.loginScreen.logo,
      ),
      supportEmail: normalizeRequiredString(
        config?.loginScreen?.supportEmail,
        fallback.loginScreen.supportEmail,
      ),
      termsAndConditions: {
        url: normalizeRequiredString(
          config?.loginScreen?.termsAndConditions?.url,
          fallback.loginScreen.termsAndConditions.url,
        ),
        hidden: normalizeBoolean(
          config?.loginScreen?.termsAndConditions?.hidden,
          fallback.loginScreen.termsAndConditions.hidden,
        ),
      },
      privacyPolicy: {
        url: normalizeRequiredString(
          config?.loginScreen?.privacyPolicy?.url,
          fallback.loginScreen.privacyPolicy.url,
        ),
        hidden: normalizeBoolean(
          config?.loginScreen?.privacyPolicy?.hidden,
          fallback.loginScreen.privacyPolicy.hidden,
        ),
      },
    },
    documentBranding: {
      logo: normalizeNullableString(
        config?.documentBranding?.logo,
        fallback.documentBranding.logo,
      ),
      footerText: normalizeRequiredString(
        config?.documentBranding?.footerText,
        fallback.documentBranding.footerText,
      ),
      watermark: normalizeNullableString(
        config?.documentBranding?.watermark,
        fallback.documentBranding.watermark,
      ),
    },
    themeAndColors: normalizeThemeAndColors(config?.themeAndColors),
    extra: {
      ...fallback.extra,
      ...config?.extra,
    },
  };
}

export function mergeBrandingConfig(
  base: BrandingConfig,
  override?: Partial<BrandingConfig> | null,
): BrandingConfig {
  if (!override) return normalizeBrandingConfig(base);

  return normalizeBrandingConfig({
    ...base,
    ...override,
    brandIdentity: {
      ...base.brandIdentity,
      ...override.brandIdentity,
      headerLogo: {
        ...base.brandIdentity.headerLogo,
        ...override.brandIdentity?.headerLogo,
      },
    },
    loginScreen: {
      ...base.loginScreen,
      ...override.loginScreen,
      termsAndConditions: {
        ...base.loginScreen.termsAndConditions,
        ...override.loginScreen?.termsAndConditions,
      },
      privacyPolicy: {
        ...base.loginScreen.privacyPolicy,
        ...override.loginScreen?.privacyPolicy,
      },
    },
    documentBranding: {
      ...base.documentBranding,
      ...override.documentBranding,
    },
    themeAndColors: {
      ...base.themeAndColors,
      ...override.themeAndColors,
    },
    extra: {
      ...base.extra,
      ...override.extra,
    },
  });
}
